import { useEffect, useMemo, useState } from 'react';
import { companionCities } from './companionCities.data';
import type { CompanionCity, CompanionRegionFilter, GlobeArc, GlobePoint } from './types';

export function useGlobalCompanionsState() {
  const [activeCityId, setActiveCityId] = useState(companionCities[0].id);
  const [activeRegion, setActiveRegion] = useState<CompanionRegionFilter>('همه');
  const [videoCity, setVideoCity] = useState<CompanionCity | null>(null);

  const filteredCities = useMemo(
    () =>
      activeRegion === 'همه'
        ? companionCities
        : companionCities.filter((city) => city.region === activeRegion),
    [activeRegion],
  );

  const activeCity = useMemo(
    () =>
      filteredCities.find((city) => city.id === activeCityId) ??
      filteredCities[0] ??
      companionCities[0],
    [activeCityId, filteredCities],
  );

  useEffect(() => {
    if (!filteredCities.some((city) => city.id === activeCityId)) {
      setActiveCityId(filteredCities[0]?.id ?? companionCities[0].id);
    }
  }, [activeCityId, filteredCities]);

  const pointData: GlobePoint[] = useMemo(
    () =>
      filteredCities.map((city, index) => ({
        ...city,
        order: index,
        size: city.id === activeCity.id ? 0.4 : 0.28,
      })),
    [activeCity.id, filteredCities],
  );

  const arcData: GlobeArc[] = useMemo(() => {
    const origin = companionCities[0];
    return filteredCities
      .filter((city) => city.id !== origin.id)
      .map((city, index) => {
        const isActive = city.id === activeCity.id;
        return {
          cityId: city.id,
          startLat: origin.lat,
          startLng: origin.lng,
          endLat: city.lat,
          endLng: city.lng,
          order: index,
          tone: city.tone,
          active: isActive,
          color: isActive
            ? ['rgba(56,189,248,0.08)', 'rgba(125,211,252,0.92)']
            : ['rgba(99,102,241,0.04)', 'rgba(129,140,248,0.42)'],
        };
      });
  }, [activeCity.id, filteredCities]);

  const totalCompanions = useMemo(
    () => companionCities.reduce((sum, city) => sum + city.companions, 0),
    [],
  );

  const activateCity = (city: CompanionCity | null | undefined) => {
    if (!city?.id) return;
    setActiveRegion(city.region);
    setActiveCityId(city.id);
  };

  const scrollToStories = () => {
    document.getElementById('companions-city-stories')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return {
    activeCity,
    activeCityId,
    activeRegion,
    setActiveRegion,
    setActiveCityId,
    setVideoCity,
    videoCity,
    filteredCities,
    pointData,
    arcData,
    totalCompanions,
    activateCity,
    scrollToStories,
  };
}
